import type { ComponentProps, FC, ReactNode } from 'react';
import { Button } from '@george-labs.com/design-system';

import { AliasChangeDropdown } from '@retail/products-customisation';
import { t } from '@luna/i18n';
import { useActiveCustomizationFeature } from '@shared/products-core';
import { CustomizationService } from '@api-client/products';

import type { ActivePAPIProductWithAvatarData } from '../types';
import { useCustomizationSuccessHandler } from './hooks/useCustomizationSuccessHandler';
import { getCustomizationWithFallback } from '../services/fallbackCustomizationValueService';
import { useActivePAPIProduct } from '../../../ActivePAPIProductContext';

interface Props {
    toggle?: ReactNode;
    trigger?: ComponentProps<typeof AliasChangeDropdown>['trigger'];
}

export const AliasChange: FC<Props> = ({ toggle, trigger = 'productDetail' }) => {
    const { activateAliasChangeCustomization } = useActiveCustomizationFeature();
    const handleSuccessfulCustomization = useCustomizationSuccessHandler();

    const {
        localizedName,
        id,
        _embedded: { customization },
    } = useActivePAPIProduct<ActivePAPIProductWithAvatarData>();

    const { name, changes } = customization;
    const aliasChangeContext = trigger === 'overview' ? 'dashboard' : 'details';

    const updateAlias = async (alias: string) =>
        CustomizationService.putPublicProductsCustomizationName({
            id,
            requestBody: { name: alias },
        }).then((customizationResponse) =>
            handleSuccessfulCustomization(
                getCustomizationWithFallback(
                    { ...customization, name: alias },
                    customizationResponse,
                ),
            ),
        );

    const aliasChangeToggle = toggle || (
        <Button
            icon={Button.ICON.EDIT}
            size={Button.SIZE.SMALL}
            onClick={activateAliasChangeCustomization}
            data-cy="product-settings-edit-alias-button"
            className="g-inverted"
        >
            {t('mainOverview.action.changeName_a11y')}
        </Button>
    );

    return (
        <AliasChangeDropdown
            alias={name}
            hasAlias={changes.includes('NAME') || !!name}
            aliasChangeContext={aliasChangeContext}
            onAliasSave={updateAlias}
            productType={localizedName}
            toggle={aliasChangeToggle}
            placeholder={localizedName}
            trigger={trigger}
        />
    );
};
